/**
 * Level-up reward overlay (HEX-117 / ADR-0018 §3).
 *
 * Shown once per level when `addXp` reports `levelsGained > 0` (a big award can
 * fire several in sequence). Presents 3 reward offers —
 * at most one specialist die slot, the rest gameplay cards — and lets the
 * player pick one. The host owns persistence; picks are applied before
 * `onPick` returns.
 *
 * Full-heal fires in the calling code before the overlay mounts. The overlay
 * only drives the pick UI.
 *
 * Layout: interaction-modal chrome, wider panel (max-width 620px), three
 * offer tiles in a horizontal flex row. Each tile is 160×200px so the card
 * chip fills its container without overflow (the HEX-111 DOM-measurement
 * invariant: `scrollHeight ≤ clientHeight` on `.hex6-levelup-offer__card`).
 */

import type { LevelUpOffer } from '../../progression/xp';
import type { DieType } from '../dice';
import { CardHoverPanel, buildCardChip } from './cardHoverPanel';
import { renderDieChip } from './dieTray';

export interface LevelUpOverlayOptions {
  readonly level: number;
  readonly offers: readonly LevelUpOffer[];
  /** Called with the chosen offer. Caller mutates character + persists before
   * resolving — overlay waits until the promise settles before showing Continue. */
  readonly onPick: (offer: LevelUpOffer) => Promise<void>;
  /** Fired when the player dismisses the resolved overlay. */
  readonly onDone: () => void;
}

export class LevelUpOverlay {
  private modal: HTMLDivElement | null = null;
  private readonly hoverPanel = new CardHoverPanel();

  constructor(private readonly container: HTMLElement) {}

  start(opts: LevelUpOverlayOptions): void {
    if (this.modal) return;
    const root = document.createElement('div');
    root.className = 'hex6-interaction-modal';
    root.dataset.role = 'level-up-overlay';
    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-label', 'Level up!');

    const panel = document.createElement('div');
    panel.className = 'hex6-interaction-modal__panel';
    panel.style.cssText = 'min-width:520px;max-width:min(640px,95vw);';

    const title = document.createElement('h2');
    title.className = 'hex6-interaction-modal__title';
    title.dataset.role = 'level-up-title';
    title.textContent = `Level Up! → Level ${opts.level}`;
    title.style.cssText = 'color:#a0d0ff;';

    const subtitle = document.createElement('div');
    subtitle.className = 'hex6-interaction-modal__body';
    subtitle.textContent = 'You have been fully healed. Pick a reward:';

    const offersRow = document.createElement('div');
    offersRow.dataset.role = 'level-up-offers';
    offersRow.style.cssText =
      'display:flex;gap:12px;justify-content:center;flex-wrap:wrap;padding:4px 0;';

    const actions = document.createElement('div');
    actions.className = 'hex6-interaction-modal__actions';

    for (const offer of opts.offers) {
      offersRow.appendChild(this.buildOfferTile(offer, opts, actions));
    }

    panel.append(title, subtitle, offersRow, actions);
    root.appendChild(panel);
    this.container.appendChild(root);
    this.modal = root;
  }

  destroy(): void {
    this.hoverPanel.destroy();
    this.modal?.remove();
    this.modal = null;
  }

  private buildOfferTile(
    offer: LevelUpOffer,
    opts: LevelUpOverlayOptions,
    actions: HTMLElement,
  ): HTMLDivElement {
    const tile = document.createElement('div');
    tile.className = 'hex6-levelup-offer';
    tile.dataset.role = 'level-up-offer';
    tile.dataset.offerKind = offer.kind;
    tile.style.cssText =
      'display:flex;flex-direction:column;align-items:center;gap:8px;width:160px;';

    const cardWrapper = document.createElement('div');
    cardWrapper.className = 'hex6-levelup-offer__card';
    cardWrapper.dataset.role = 'level-up-offer-card';
    cardWrapper.style.cssText = 'width:160px;height:200px;overflow:hidden;';

    if (offer.kind === 'card') {
      const chip = buildCardChip(offer.card, this.hoverPanel);
      chip.style.cssText = 'width:100%;height:100%;box-sizing:border-box;';
      cardWrapper.appendChild(chip);
    } else {
      cardWrapper.appendChild(this.buildDieTile(offer.die));
    }

    const pickBtn = document.createElement('button');
    pickBtn.type = 'button';
    pickBtn.dataset.action = 'level-up-pick';
    pickBtn.className = 'hex6-interaction-modal__button hex6-interaction-modal__button--primary';
    pickBtn.textContent = offer.kind === 'card' ? offer.card.name : offer.die.name;
    pickBtn.style.cssText = 'width:100%;';

    pickBtn.addEventListener('click', () => {
      void this.resolvePick(offer, opts, actions);
    });

    tile.append(cardWrapper, pickBtn);
    return tile;
  }

  /** The die reward reads as a DIE, not a card-styled tile (ADR-0022 §4 /
   * HEX-139) — the shared dice-tray chip, sized to fill the offer wrapper. */
  private buildDieTile(die: DieType): HTMLElement {
    return renderDieChip(die, { variant: 'offer' });
  }

  private async resolvePick(
    offer: LevelUpOffer,
    opts: LevelUpOverlayOptions,
    actions: HTMLElement,
  ): Promise<void> {
    // Disable all pick buttons while resolving.
    const modal = this.modal;
    if (!modal) return;
    for (const btn of modal.querySelectorAll<HTMLButtonElement>('[data-action="level-up-pick"]')) {
      btn.disabled = true;
    }
    // Hide this overlay while onPick drives the acquisition drawer (HEX-149) so the
    // offer-beside-loadout surface isn't stacked under the pick modal.
    modal.style.visibility = 'hidden';
    await opts.onPick(offer);
    if (this.modal) this.modal.style.visibility = '';
    this.showContinue(opts, actions);
  }

  private showContinue(opts: LevelUpOverlayOptions, actions: HTMLElement): void {
    actions.replaceChildren();
    const done = document.createElement('button');
    done.type = 'button';
    done.dataset.action = 'level-up-done';
    done.className = 'hex6-interaction-modal__button hex6-interaction-modal__button--primary';
    done.textContent = 'Continue';
    done.addEventListener('click', () => opts.onDone());
    actions.appendChild(done);
  }
}
